import { useEffect, useRef, useMemo } from 'react';
import { MapboxOverlay } from '@deck.gl/mapbox';
import type { Map as MapLibreMap } from 'maplibre-gl';
import { useDeckLayers } from '@/hooks/useDeckLayers';
import { useWeatherLayers } from '@/hooks/useWeatherLayers';
import { useWindLayer } from '@/hooks/useWindLayer';

interface DeckOverlayProps {
  map: MapLibreMap;
}

export function DeckOverlay({ map }: DeckOverlayProps) {
  const overlayRef = useRef<MapboxOverlay | null>(null);
  const { layers: annotationLayers, handleClick, handleHover } = useDeckLayers();
  const { layers: weatherLayers } = useWeatherLayers();

  // Wind is now a canvas overlay, not Deck.gl layers.
  // It just needs the map instance.
  useWindLayer(map);

  // Combine layers: weather alerts then annotations (top)
  const layers = useMemo(
    () => [...weatherLayers, ...annotationLayers],
    [weatherLayers, annotationLayers]
  );

  // Store callbacks in refs to avoid re-creating overlay
  const handleClickRef = useRef(handleClick);
  const handleHoverRef = useRef(handleHover);
  handleClickRef.current = handleClick;
  handleHoverRef.current = handleHover;

  // Initialize Deck.gl overlay once
  useEffect(() => {
    if (!map || overlayRef.current) return;

    const overlay = new MapboxOverlay({
      interleaved: true,
      layers: [],
      onClick: (info, event) => {
        if (event.srcEvent?.shiftKey) {
          event.srcEvent.preventDefault();
          event.srcEvent.stopPropagation();
        }
        if (info.picked) {
          handleClickRef.current(info, event);
        }
      },
      onHover: (info) => {
        handleHoverRef.current(info);
        map.getCanvas().style.cursor = info.picked ? 'pointer' : '';
      },
    });

    map.addControl(overlay as unknown as maplibregl.IControl);
    overlayRef.current = overlay;

    return () => {
      if (overlayRef.current) {
        map.removeControl(overlayRef.current as unknown as maplibregl.IControl);
        overlayRef.current = null;
      }
    };
  }, [map]);

  // Update layers when they change
  useEffect(() => {
    if (overlayRef.current) {
      overlayRef.current.setProps({ layers });
    }
  }, [layers]);

  return null;
}
